import { mapElements } from "../../util/Utils";
import { copyLabels } from "../../tracking/FeatureTracking";
import { HeurekaElementFactory } from "../../util/HeurekaElementFactory";
import { UpdateReferencedAction } from "../UpdateReferencedAction";
import { events } from "../../events";
import { FilterTitle } from "../FilterTitle";

const SELECT_ALL_BUTTON_CLASS = "ts_heureka_selectAll";

const UNCHECKED_VISIBLE_VALUES =
  "li:not([hidden]) input[type=checkbox].js_tValue:not(:checked), li:not([hidden]) input[type=checkbox].js_hc_tValue:not(:checked)";
const CHECKED_VISIBLE_CHECKBOXES =
  "li:not([hidden]) input[type=checkbox].js_tValue:checked, li:not([hidden]) input[type=checkbox].js_hc_tValue:checked";
const CHECKED_VISIBLE_RADIOS =
  "li:not([hidden]) input[type=radio].js_tValue:checked, li:not([hidden]) input[type=radio].js_hc_tValue:checked";
const ACTIVE_RANGES = "input[type=text].js_tValue:not([value='_,_']), input[type=text].js_hc_tValue:not([value='_,_'])";
const ACTIVE_VISIBLE_VALUES = `${CHECKED_VISIBLE_CHECKBOXES},${CHECKED_VISIBLE_RADIOS},${ACTIVE_RANGES}`;

/*                                                                               */
function matchingIds(a: string, b: string): boolean {
  return a.includes(b) || b.includes(a);
}

export class SelectAllButton {
  static readonly factory = HeurekaElementFactory.byElement("button", SELECT_ALL_BUTTON_CLASS, SelectAllButton);

  /*               */

  constructor(
    readonly button: HTMLButtonElement,
    readonly deselectMode = SelectAllButton.deselectMode(button.dataset.selectMode),
  ) {
    this.button = button;
    this.deselectMode = deselectMode;
  }

  private static deselectMode(mode?: string) {
    return mode === "deselect";
  }

  /*               */

  static initAll(rootElement?: ParentNode) {
    SelectAllButton.factory.forEach(SelectAllButton.init, rootElement);
  }

  private static init(elem: SelectAllButton) {
    elem.registerOnFormChange();
    elem.registerOnClick();
    elem.registerOnCustomRangeInput();
  }

  private registerOnFormChange() {
    const { form } = this.button;
    if (form) {
      const listener = this.formChanged.bind(this);
      const options = { passive: true };
      form.addEventListener("change", listener, options);
      form.addEventListener("input", listener, options);
      form.addEventListener("reset", listener, options);
    }
    return this;
  }

  private registerOnClick() {
    const { form } = this.button;
    this.button.addEventListener("click", this.clicked.bind(this));
    this.button.addEventListener("click", (event) => {
      event.target.dispatchEvent(
        new CustomEvent("heureka.removeAllTrashCan.clicked", { detail: { filterId: `${form.id}` }, bubbles: true }),
      );
      /*                                                                                      */
      this.hide();
      FilterTitle.factory.closest(this.button).validate();
    });
    return this;
  }

  /*                       */

  show() {
    this.button.hidden = false;
  }

  hide() {
    this.button.hidden = true;
  }

  toggleVisibility(force = !this.button.hidden) {
    this.button.hidden = force;
    return this;
  }

  toggleInputs() {
    return this.deselectMode ? this.deactivateActiveValues() : this.checkUncheckedValues();
  }

  isSelectAll() {
    return !this.deselectMode;
  }

  /*                 */

  private formChanged() {
    /*          */
    this.toggleVisibility(this.deselectMode != this.hasAnyVisibleActiveValue());
  }

  private clicked(event: Event) {
    const changedItems = this.toggleInputs().map(copyLabels.bind(this, this.button)).length;
    if (changedItems) {
      this.triggerChange();
    }
    event.stopPropagation();
  }

  /*       */

  /*                                                     */

  private hasAnyVisibleActiveValue(form: HTMLFormElement | null = this.button.form) {
    return !!form?.querySelector(ACTIVE_VISIBLE_VALUES);
  }

  private checkUncheckedValues(form: HTMLFormElement | null = this.button.form) {
    return mapElements<HTMLInputElement, HTMLInputElement>(
      UNCHECKED_VISIBLE_VALUES,
      (elem) => {
        elem.checked = true;
        UpdateReferencedAction.updateAllValues(elem);
        return elem;
      },
      form || undefined,
    );
  }

  private deactivateActiveValues(form: HTMLFormElement | null = this.button.form) {
    return mapElements<HTMLInputElement, HTMLInputElement>(
      ACTIVE_VISIBLE_VALUES,
      (elem) => SelectAllButton.clearInput(elem, form?.id),
      form || undefined,
    );
  }

  private triggerChange(form: HTMLFormElement | null = this.button.form) {
    form?.dispatchEvent(new Event("change", { bubbles: true }));
  }

  /*                                                      */

  private static clearInput(elem: HTMLInputElement, formId?: string): HTMLInputElement {
    const type = elem.type ? elem.type : elem.getAttribute("type");
    /*                                                            */
    /*                                                                    */
    /*                                                                                */
    if (formId == "find_filter_preis") {
      elem.value = "_,_";
      return elem;
    }

    switch (type) {
      case "text":
        elem.value = "_,_";
        break;
      case "radio":
      case "checkbox":
        elem.checked = false;
        UpdateReferencedAction.updateAllValues(elem);
        break;
    }
    return elem;
  }

  private registerOnCustomRangeInput() {
    document.addEventListener("heureka.customRange.onInput", (event) => {
      const customEvent = event as CustomEvent<{
        filterId: string;
      }>;
      const eventFilterId = customEvent.detail?.filterId;
      const { form } = this.button;
      /*                                                           */
      if (eventFilterId && form && matchingIds(form.id, eventFilterId)) {
        this.show();
      }
    });
  }
}

function onFiltersLoaded() {
  SelectAllButton.initAll();
}

export function registerFilterFormSelectAllButtons() {
  events.onFilterSectionLoaded(onFiltersLoaded);
}
